import { useState, useEffect } from 'react';
import { FileText, Download, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import Header from '../components/layout/Header';
import { reportApi } from '../services/api';
import type { Report } from '../types';

const statusConfig = {
  pending: { icon: Clock, color: 'text-gray-400', label: 'Pending' },
  processing: { icon: Loader2, color: 'text-signal-yellow', label: 'Processing' },
  ready: { icon: CheckCircle, color: 'text-signal-green', label: 'Ready' },
  failed: { icon: AlertCircle, color: 'text-signal-red', label: 'Failed' },
};

export default function ReportsPage() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');

  useEffect(() => {
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await reportApi.listReports(50, 0, filter || undefined);
      setReports(response.reports);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header title="Reports" subtitle="Your generated PDF reports" />

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Filters */}
        <div className="card">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Filter by status:</span>
            <div className="flex gap-2">
              {['', 'ready', 'processing', 'failed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    filter === status
                      ? 'bg-teal text-white'
                      : 'bg-teal/10 text-gray-400 hover:text-white'
                  }`}
                >
                  {status || 'All'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Reports List */}
        <div className="card">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-teal animate-spin mx-auto" />
              <p className="text-gray-500 mt-2">Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-600" />
              <p className="text-lg font-medium">No reports yet</p>
              <p className="text-sm mt-1">
                Generate your first report from a domain scan
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-teal/20">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
                      Domain
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
                      Created
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">
                      Downloaded
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => {
                    const status = statusConfig[report.status];
                    const StatusIcon = status.icon;

                    return (
                      <tr
                        key={report.id}
                        className="border-b border-teal/10 hover:bg-teal/5 transition-colors"
                      >
                        <td className="py-3 px-4 font-mono text-sm">
                          {report.domain}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`flex items-center gap-2 text-sm ${status.color}`}>
                            <StatusIcon
                              size={16}
                              className={report.status === 'processing' ? 'animate-spin' : ''}
                            />
                            {status.label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-400">
                          {new Date(report.createdAt).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-400">
                          {report.downloadedAt
                            ? new Date(report.downloadedAt).toLocaleString()
                            : '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {report.status === 'ready' && report.downloadUrl && (
                            <a
                              href={report.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-secondary text-sm py-1.5 px-3 inline-flex items-center gap-1.5"
                            >
                              <Download size={14} />
                              Download
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
